import { userServiceApiSlice } from "state/services/userServiceApiSlice";

// Statuses a user's own budget request can end up in — used to invalidate
// every cached "My Requests" status filter bucket at once, since we don't
// track which bucket(s) a given request currently lives in client-side.
const MY_BUDGET_REQUEST_STATUSES = ["all", "pending", "approved", "rejected", "cancelled"];

// Statuses the admin "AIC Budget Requests" table can filter by — no
// "cancelled" here, since cancellation is a user-side action on their own
// pending request, not a state the admin table's endpoint supports.
const ADMIN_BUDGET_REQUEST_STATUSES = ["all", "pending", "approved", "rejected"];
const ADMIN_BUDGET_REQUEST_ACTIONS = ["all", "increase", "decrease", "admin_set"];

// The `userServiceApiMutations` object provides hooks that allow you to make mutations to the user service API.
export const userServiceApiMutations = userServiceApiSlice.injectEndpoints({
  // The `endpoints` object is used to define the API endpoints for the user service.
  endpoints: (builder) => ({
    // The `updateUser` mutation is used to update a user entry.
    updateUser: builder.mutation({
      query: (body) => ({
        url: "/users",
        method: "PUT",
        body: body,
      }),
      transformResponse: (responseData) => {
        return responseData.updated[0];
      },
      invalidatesTags: ["User"],
    }),
    // The `createUser` mutation is used to create a new user entry.
    createUser: builder.mutation({
      query: (body) => ({
        url: "/users",
        method: "POST",
        body: body,
      }),
      transformResponse: (responseData) => {
        return responseData;
      },
      invalidatesTags: ["User"],
    }),
    // The `updateUserOrgStatus` mutation is used to update single user's organization membership status.
    updateUserOrgStatus: builder.mutation({
      query: ({ userId, body, attribute }) => ({
        url: `/users/${userId}/${attribute}`,
        method: "PATCH",
        body: body,
      }),
      transformResponse: (responseData) => {
        return responseData;
      },
      invalidatesTags: ["User"],
    }),

    // The `updateUserMembershipBulk` mutation is used to bulk update multiple users' membership status in a single API call.
    updateUserMembershipBulk: builder.mutation({
      query: ({ body }) => ({
        url: `/users/bulk-update-membership`,
        method: "POST",
        body: body,
      }),
      transformResponse: (responseData) => {
        return responseData;
      },
      invalidatesTags: (result, error, { invalidateStatuses }) =>
        (invalidateStatuses || []).map(
          (status) => ({ type: "User", id: `OrgRequests-${status}` })
        ),
    }),

    updateUserOrgStatusBatchWithExcel: builder.mutation({
      query: ({ body, url }) => ({
        url: url || `/users/bulkupload`,
        method: "POST",
        body: body,
      }),
      transformResponse: (responseData) => {
        return responseData;
      },
      invalidatesTags: ["User"],
    }),

    updateUserOrgStatusBatch: builder.mutation({
      async queryFn(
        { rowData, platformId, orgId, rejectReason, otherReason, userEmail },
        _queryApi,
        _extraOptions,
        baseQuery
      ) {
        for (const row of rowData) {
          const body = {
            platforms: {
              [platformId]: {
                organizations: {
                  [orgId]: {
                    membership_status:
                      row.status === "joined"
                        ? "Remove-Requested"
                        : "Not-Requested",
                    reason:
                      rejectReason === "Other" ? otherReason : rejectReason,
                    changed_by: userEmail.toLowerCase(),
                  },
                },
              },
            },
          };

          const res = await baseQuery({
            url: `/users/${row?.id}/membership_status`,
            method: "PATCH",
            body: body,
          });

          if (res.error) {
            return { error: res.error };
          }
        }
        return { data: rowData.length };
      },
      invalidatesTags: ["User"],
    }),

    triggerOrgUserSync: builder.mutation({
      query: ({ orgId }) => ({
        url: `/sync/tasks/trigger/org/${orgId}/verify-pending?queue=sync.user`,
        method: "POST",
      }),
      transformResponse: (responseData) => {
        return responseData;
      },
    }),
    reactivateUser: builder.mutation({
      query: (body) => ({
        url: "/users/usertracker",
        method: "POST",
        body,
      }),
    }),
    // The `createBudgetChangeRequest` mutation submits a request to increase or
    // decrease the signed-in user's AI Credit budget. `orgId` is only used by
    // the backend for routing/notification context — the budget itself is
    // resolved per the user's effective scope (enterprise/cost-center/user).
    createBudgetChangeRequest: builder.mutation({
      query: ({ email, platformId, orgId, action, amount, reason }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}/request?platform_id=${encodeURIComponent(platformId)}`,
        method: "POST",
        body: {
          change_amount: amount,
          action,
          org_id: orgId,
          request_reason: reason,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "User", id: "Budget" },
        ...MY_BUDGET_REQUEST_STATUSES.map((status) => ({
          type: "User",
          id: `MyBudgetRequests-${status}`,
        })),
      ],
    }),
    // The `reviewBudgetRequests` mutation approves or rejects one or more
    // pending AI Credit budget requests from the admin "AIC Budget Requests"
    // table — `requests` is just a list, so a single-item array covers the
    // per-row Approve/Reject buttons too. Invalidates every cached
    // action/status filter combo of the requests list, since a reviewed
    // request moves out of "pending" into whichever list its new status
    // belongs to.
    reviewBudgetRequests: builder.mutation({
      query: ({ platformId, orgId, reviewedBy, actionReason, requests }) => ({
        url: `/users/copilot/budget-requests/bulk-review?platform_id=${encodeURIComponent(platformId)}`,
        method: "POST",
        body: {
          org_id: orgId,
          reviewed_by: reviewedBy,
          action_reason: actionReason,
          requests,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        ...ADMIN_BUDGET_REQUEST_STATUSES.flatMap((status) =>
          ADMIN_BUDGET_REQUEST_ACTIONS.map((action) => ({
            type: "User",
            id: `BudgetRequests-${action}-${status}`,
          }))
        ),
        // Also bust the reviewed users' own "My Requests" history caches —
        // we don't know which users are cached client-side, so invalidate
        // every status bucket rather than track per-user tags.
        ...MY_BUDGET_REQUEST_STATUSES.map((status) => ({
          type: "User",
          id: `MyBudgetRequests-${status}`,
        })),
        // An approved request changes the reviewed user's actual budget cap —
        // bust getUserBudget's cache too, or the "Manage My AI Credit Budget"
        // modal keeps showing the pre-approval cap until it's closed/reopened.
        { type: "User", id: "Budget" },
      ],
    }),
    // The `bulkSetCopilotBudget` mutation is an admin direct-set of AI Credit
    // budget for multiple users at once — bypasses the increase/decrease
    // request flow entirely (no approval needed, applies immediately).
    // Partial success is possible: the response reports succeeded/failed
    // per email rather than failing the whole batch on one bad email.
    bulkSetCopilotBudget: builder.mutation({
      query: ({ platformId, orgId, emails, budgetAmount, approvedBy, preventFurtherUsage, willAlert }) => ({
        url: `/users/copilot/budget/bulk?platform_id=${encodeURIComponent(platformId)}`,
        method: "POST",
        body: {
          org_id: orgId,
          emails,
          budget_amount: budgetAmount,
          approved_by: approvedBy,
          prevent_further_usage: preventFurtherUsage,
          will_alert: willAlert,
        },
      }),
      invalidatesTags: [{ type: "User", id: "Budget" }],
    }),
    // The `cancelBudgetRequest` mutation lets a user cancel their own pending
    // AI Credit budget request — a soft delete, status moves to "cancelled"
    // rather than being removed from their history.
    cancelBudgetRequest: builder.mutation({
      query: ({ email, platformId }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}/request?platform_id=${encodeURIComponent(platformId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "User", id: "Budget" },
        ...MY_BUDGET_REQUEST_STATUSES.map((status) => ({
          type: "User",
          id: `MyBudgetRequests-${status}`,
        })),
      ],
    }),
  }),
});

export const {
  useUpdateUserMutation,
  useCreateUserMutation,
  useUpdateUserOrgStatusMutation,
  useUpdateUserOrgStatusBatchMutation,
  useUpdateUserOrgStatusBatchWithExcelMutation,
  useUpdateUserMembershipBulkMutation,
  useTriggerOrgUserSyncMutation,
  useReactivateUserMutation,
  useCreateBudgetChangeRequestMutation,
  useReviewBudgetRequestsMutation,
  useBulkSetCopilotBudgetMutation,
  useCancelBudgetRequestMutation,
} = userServiceApiMutations;
